import React from "react";
import { ErrorDoodle } from "@/components/ErrorDoodle";
import * as Sentry from "@sentry/react";

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

export function ConnectionError() {
  React.useEffect(() => {
    const captureConnectionError = () => {
      const connectionInfo: Record<string, unknown> = {
        online: navigator.onLine,
        userAgent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // Try to get network information if available
      if ("connection" in navigator) {
        const nav = navigator as Navigator & { connection?: NetworkInformation };
        if (nav.connection) {
          connectionInfo.effectiveType = nav.connection.effectiveType;
          connectionInfo.downlink = nav.connection.downlink;
          connectionInfo.rtt = nav.connection.rtt;
          connectionInfo.saveData = nav.connection.saveData;
        }
      }

      // Check for common browser security features that might block requests
      connectionInfo.hasContentSecurityPolicy = "securityPolicy" in document;
      connectionInfo.hasCrossOriginIsolated = "crossOriginIsolated" in window;

      Sentry.captureMessage("Connection Error Page Displayed", {
        level: "error",
        tags: {
          errorType: "connection_error",
        },
        extra: connectionInfo,
      });
    };

    captureConnectionError();
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-backgroundGray">
      <div className="flex flex-col items-center space-y-6 gap-y-4">
        <ErrorDoodle />
        <div className="font-medium text-[32px] text-center">Connection Error</div>
      </div>
      <div className="max-w-2xl px-4 text-feintGray font-medium text-center space-y-4">
        <p>We're unable to connect to the server. This could be due to:</p>
        <ul className="list-disc list-inside text-left space-y-2">
          <li>No internet connection or unstable network</li>
          <li>Server is temporarily down or under maintenance</li>
          <li>Your request timed out</li>
          <li>DNS resolution issues</li>
          <li>Firewall or security software blocking the connection</li>
          <li>Proxy or VPN configuration issues</li>
        </ul>
        <p className="mt-4">
          Please check your connection and try again. If the problem persists, contact support.
        </p>
      </div>
    </div>
  );
}
